// src/modules/ida/services/api.ts
//
// API service hooks for IDA module - document operations.
// Note: Chat functionality is now provided by @metalmind-ab/mmagent-chat-ui.

import { useCallback } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type {
  IDADocument,
  IDAWorkspaceContext,
  IDAVersionHistory,
  ExportJob,
  ExportZipResponse,
} from "../common/types";
import type { SkillInvocationAttributionResponse } from "../../../common/types";
import { toInternalDownloadLinkEndpoint } from "../utils/internalDownloadLinks";

const API_BASE = "/api/workspace";

/**
 * Hook to list IDA documents in a workspace.
 */
export const useListDocuments = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string): Promise<IDADocument[]> => {
      // Request up to 10000 documents to avoid pagination issues
      const response = await authFetch(
        `${API_BASE}/ida/documents/?workspace_id=${workspaceId}&limit=10000`,
        "fetch IDA documents",
      );
      return response.documents || [];
    },
    [authFetch],
  );
};

/**
 * Hook to upload a document to IDA.
 */
export const useUploadDocument = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      file: File,
      folder?: string,
    ): Promise<IDADocument> => {
      const formData = new FormData();
      formData.append("file", file);

      // Build URL with optional folder parameter
      let url = `${API_BASE}/ida/documents/upload?workspace_id=${workspaceId}`;
      if (folder) {
        url += `&folder=${encodeURIComponent(folder)}`;
      }

      return await authFetch(
        url,
        `upload IDA document ${file.name}`,
        {
          method: "POST",
          body: formData,
        },
        60000, // Longer timeout for uploads
      );
    },
    [authFetch],
  );
};

/**
 * Hook to delete an IDA document.
 */
export const useDeleteDocument = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
    ): Promise<{ deleted: boolean; doc_id: string }> => {
      // URL encode the docId in case it contains slashes
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}?workspace_id=${workspaceId}`,
        "delete IDA document",
        {
          method: "DELETE",
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to delete multiple IDA documents.
 */
export const useDeleteDocuments = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docIds: string[],
    ): Promise<{
      deleted: string[];
      failed: Array<{ doc_id: string; error: string }>;
    }> => {
      return await authFetch(
        `${API_BASE}/ida/documents/delete-batch?workspace_id=${workspaceId}`,
        "delete IDA documents",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ doc_ids: docIds }),
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to download an IDA document.
 * Returns a function that either triggers a file download or yields the blob.
 */
export interface DownloadDocumentOptions {
  responseType?: "download" | "blob";
  /** An earlier SharePoint version of a SharePoint-backed document. */
  spVersionId?: string;
}

export interface DocumentDownloadLink {
  doc_id: string;
  filename: string;
  download_url: string;
  expires_in: number;
  expires_in_formatted: string;
  mime_type?: string;
}

export const useResolveDocumentDownloadLink = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (href: string): Promise<DocumentDownloadLink> => {
      const endpoint = toInternalDownloadLinkEndpoint(href);
      if (!endpoint) {
        throw new Error("Not an internal IDA document download link");
      }
      return await authFetch(endpoint, "resolve IDA document download link");
    },
    [authFetch],
  );
};

export const useDownloadDocument = () => {
  // Use raw response type for binary downloads
  const authFetch = useAuthFetch("raw");

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
      filename: string,
      options: DownloadDocumentOptions = {},
    ): Promise<void | Blob> => {
      // URL encode the docId in case it contains slashes
      const encodedDocId = encodeURIComponent(docId);
      const versionParam = options.spVersionId
        ? `&sp_version_id=${encodeURIComponent(options.spVersionId)}`
        : "";
      // Fetch the file as raw response
      const response = await authFetch(
        `${API_BASE}/ida/documents/download/${encodedDocId}?workspace_id=${workspaceId}${versionParam}`,
        `download document ${filename}`,
        {},
        60000, // Longer timeout for downloads
      );

      // Create blob from response
      const blob = await response.blob();

      if (options.responseType === "blob") {
        return blob;
      }

      // Prefer filename from Content-Disposition header (set by backend)
      let downloadName = filename;
      const disposition = response.headers.get("Content-Disposition");
      if (disposition) {
        // Try filename*= (RFC 5987 UTF-8 encoded) first, then filename=
        const utf8Match = disposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
        const plainMatch = disposition.match(/filename="?([^";\n]+)"?/i);
        const extracted = utf8Match?.[1] || plainMatch?.[1];
        if (extracted) downloadName = decodeURIComponent(extracted.trim());
      }

      // Create temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return undefined;
    },
    [authFetch],
  );
};

/**
 * Hook to get workspace context.
 */
export const useGetContext = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string): Promise<IDAWorkspaceContext> => {
      return await authFetch(
        `${API_BASE}/ida/context/?workspace_id=${workspaceId}`,
        "fetch IDA workspace context",
      );
    },
    [authFetch],
  );
};

/**
 * Hook to update workspace context.
 */
export const useUpdateContext = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      updates: Partial<
        Pick<IDAWorkspaceContext, "purpose" | "user_notes" | "key_entities">
      >,
    ): Promise<IDAWorkspaceContext> => {
      return await authFetch(
        `${API_BASE}/ida/context/?workspace_id=${workspaceId}`,
        "update IDA workspace context",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updates),
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to fetch conversation skill invocation attribution rows.
 */
export const useConversationSkillAttributions = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      conversationId: number,
      turnId?: number,
    ): Promise<SkillInvocationAttributionResponse> => {
      const params = new URLSearchParams();
      if (turnId !== undefined) {
        params.set("turn_id", String(turnId));
      }
      const suffix = params.toString() ? `?${params.toString()}` : "";
      return await authFetch(
        `${API_BASE}/ida/chat/conversations/${conversationId}/skill-attributions${suffix}`,
        "fetch conversation skill attributions",
      );
    },
    [authFetch],
  );
};

/**
 * Response of the follow-up suggestions endpoint. The list is empty when the
 * workspace switch is off, the newest message is not a completed answer, or
 * generation failed.
 */
export interface FollowUpSuggestionsResponse {
  suggestions: string[];
}

/**
 * Hook to fetch follow-up question suggestions for the newest answer.
 */
export const useFollowUpSuggestions = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      conversationId: number,
      signal?: AbortSignal,
      answerMessageId?: number,
    ): Promise<FollowUpSuggestionsResponse> => {
      // The saved answer id lets the server return the follow-ups it already
      // started generating when that answer completed.
      const query =
        answerMessageId !== undefined
          ? `?answer_message_id=${encodeURIComponent(answerMessageId)}`
          : "";
      // "fetch" in the tag keeps failures out of the error toast: chips are
      // optional and must never interrupt the conversation.
      return await authFetch(
        `${API_BASE}/ida/chat/conversations/${conversationId}/follow-up-suggestions${query}`,
        "fetch follow-up suggestions",
        {
          method: "POST",
          headers: {
            "X-Workspace-Id": workspaceId,
          },
          signal,
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to fetch starter questions for a new, empty chat in a workspace.
 */
export const useStarterSuggestions = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      signal?: AbortSignal,
    ): Promise<FollowUpSuggestionsResponse> => {
      // "fetch" in the tag keeps failures out of the error toast.
      return await authFetch(
        `${API_BASE}/ida/chat/starter-suggestions`,
        "fetch starter suggestions",
        {
          method: "POST",
          headers: {
            "X-Workspace-Id": workspaceId,
          },
          signal,
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to get version history for a document.
 */
export const useGetVersionHistory = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string, docId: string): Promise<IDAVersionHistory> => {
      // URL encode the docId in case it contains slashes
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/versions?workspace_id=${workspaceId}`,
        "fetch document version history",
      );
    },
    [authFetch],
  );
};

/** Line-based text of a Word document version, for comparing versions. */
export interface DocumentDiffText {
  doc_id: string;
  filename: string;
  text: string;
}

/**
 * Hook to fetch a Word (.docx) version as line-based text, so two versions can
 * be diffed like text files. For SharePoint-backed documents, pass the
 * document id and the SharePoint version id.
 */
export const useGetDocumentDiffText = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
      options: { spVersionId?: string } = {},
    ): Promise<DocumentDiffText> => {
      // URL encode the docId in case it contains slashes
      const encodedDocId = encodeURIComponent(docId);
      const versionParam = options.spVersionId
        ? `&sp_version_id=${encodeURIComponent(options.spVersionId)}`
        : "";
      // "fetch" in the tag keeps failures out of the error toast: the diff
      // view shows its own error.
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/diff-text?workspace_id=${workspaceId}${versionParam}`,
        "fetch document text for comparison",
      );
    },
    [authFetch],
  );
};

/**
 * Hook to restore an older document version.
 * Creates a new version with the content from the specified version.
 */
export const useRestoreVersion = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
      spVersionId?: string,
    ): Promise<{
      success: boolean;
      new_doc_id: string;
      restored_from_version: number;
      new_version: number;
      filename: string;
      folder: string;
    }> => {
      // URL encode the docId in case it contains slashes
      const encodedDocId = encodeURIComponent(docId);
      const spParam = spVersionId
        ? `&sp_version_id=${encodeURIComponent(spVersionId)}`
        : "";
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/restore?workspace_id=${workspaceId}${spParam}`,
        "restore document version",
        {
          method: "POST",
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to get document content for editing.
 * For docx files, returns markdown-converted content.
 */
export const useGetDocumentForEdit = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
    ): Promise<{
      id: string;
      filename: string;
      full_text: string;
      original_format?: string;
      version: number;
    }> => {
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}?workspace_id=${workspaceId}&for_edit=true`,
        "fetch document for edit",
      );
    },
    [authFetch],
  );
};

/**
 * Hook to save edited document content.
 * Creates a new version with the updated content.
 */
export const useSaveDocumentContent = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
      content: string,
    ): Promise<{
      success: boolean;
      new_doc_id: string;
      new_version: number;
      filename: string;
      folder: string;
      format: string;
    }> => {
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/content?workspace_id=${workspaceId}`,
        "save document content",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ content }),
        },
      );
    },
    [authFetch],
  );
};

export interface UploadDocumentVersionResult {
  success: boolean;
  new_doc_id: string;
  new_version: number;
  filename: string;
  folder: string;
  index_status: string;
  backend?: "sharepoint";
}

/**
 * Hook to upload a file as the next version of an existing document.
 * The document keeps its name; the file must be the same type.
 */
export const useUploadDocumentVersion = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
      file: File,
    ): Promise<UploadDocumentVersionResult> => {
      const formData = new FormData();
      formData.append("file", file);
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/new-version?workspace_id=${workspaceId}`,
        `upload new version of ${file.name}`,
        {
          method: "POST",
          body: formData,
        },
        60000,
      );
    },
    [authFetch],
  );
};

// ============================================================================
// Export ZIP Hooks
// ============================================================================

/**
 * Hook to start a ZIP export job.
 * Returns immediately with a job_id for status tracking.
 */
export const useStartExportZip = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docIds: string[] | null,
    ): Promise<ExportZipResponse> => {
      return await authFetch(
        `${API_BASE}/ida/documents/export-zip?workspace_id=${workspaceId}`,
        "start ZIP export",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ doc_ids: docIds }),
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to get export job status.
 */
export const useGetExportStatus = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string, jobId: string): Promise<ExportJob> => {
      return await authFetch(
        `${API_BASE}/ida/documents/export-zip/${jobId}?workspace_id=${workspaceId}`,
        "get export status",
      );
    },
    [authFetch],
  );
};

/**
 * Hook to list all export jobs.
 */
export const useListExportJobs = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string): Promise<{ jobs: ExportJob[] }> => {
      return await authFetch(
        `${API_BASE}/ida/documents/export-zip?workspace_id=${workspaceId}`,
        "list export jobs",
      );
    },
    [authFetch],
  );
};

/**
 * Hook to download a completed export.
 * Triggers a browser download of the ZIP file.
 */
export const useDownloadExport = () => {
  const authFetch = useAuthFetch("raw");

  return useCallback(
    async (
      workspaceId: string,
      jobId: string,
      filename?: string,
    ): Promise<void> => {
      const response = await authFetch(
        `${API_BASE}/ida/documents/export-zip/${jobId}/download?workspace_id=${workspaceId}`,
        "download export",
        {},
        120000, // 2 minute timeout for large downloads
      );

      // Create blob from response
      const blob = await response.blob();

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let downloadFilename =
        filename ||
        `documents_export_${new Date().toISOString().slice(0, 10)}.zip`;
      if (contentDisposition) {
        // Try to extract filename from header (handles with/without quotes)
        const match = contentDisposition.match(
          /filename="([^"]+)"|filename=([^\s;]+)/,
        );
        if (match) {
          downloadFilename = match[1] || match[2];
          // Remove trailing quote if present
          downloadFilename = downloadFilename.replace(/"$/, "");
        }
      }

      // Trigger browser download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [authFetch],
  );
};

/**
 * Hook to dismiss/delete an export job.
 */
export const useDismissExportJob = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      jobId: string,
    ): Promise<{ deleted: boolean }> => {
      return await authFetch(
        `${API_BASE}/ida/documents/export-zip/${jobId}?workspace_id=${workspaceId}`,
        "dismiss export job",
        {
          method: "DELETE",
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to move documents to a different folder.
 */
export const useMoveDocuments = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docIds: string[],
      targetFolder: string,
    ): Promise<{
      moved: string[];
      failed: { doc_id: string; error: string }[];
    }> => {
      return await authFetch(
        `${API_BASE}/ida/documents/move?workspace_id=${workspaceId}`,
        "move documents",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            doc_ids: docIds,
            target_folder: targetFolder,
          }),
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to list persisted folders.
 */
export const useListFolders = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string): Promise<string[]> => {
      const response = await authFetch(
        `${API_BASE}/ida/documents/folders?workspace_id=${workspaceId}`,
        "list folders",
      );
      return response.folders || [];
    },
    [authFetch],
  );
};

/**
 * Hook to create an empty folder.
 */
export const useCreateFolder = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      folderPath: string,
    ): Promise<{ created: boolean; folder_path: string }> => {
      return await authFetch(
        `${API_BASE}/ida/documents/folders?workspace_id=${workspaceId}`,
        "create folder",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ folder_path: folderPath }),
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to delete a persisted folder record.
 */
export const useDeleteFolder = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      folderPath: string,
    ): Promise<{ deleted: boolean; folder_path: string }> => {
      return await authFetch(
        `${API_BASE}/ida/documents/folders?workspace_id=${workspaceId}&folder_path=${encodeURIComponent(folderPath)}`,
        "delete folder",
        {
          method: "DELETE",
        },
      );
    },
    [authFetch],
  );
};

/**
 * Resolved asset info returned by the resolve-assets endpoint.
 */
export interface ResolvedAsset {
  data_uri: string;
  content_type: string;
  content?: string; // Raw text content for text-based assets
}

/**
 * Hook to resolve relative asset filenames to inline data.
 * Used by HTML preview to load linked assets (images, scripts, CSS, etc.).
 * Returns data URIs and optionally raw text content to avoid CORS issues.
 */
export const useResolveAssets = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      filenames: string[],
      documentFolder?: string,
    ): Promise<Record<string, ResolvedAsset>> => {
      const response = await authFetch(
        `${API_BASE}/ida/documents/resolve-assets?workspace_id=${workspaceId}`,
        "resolve asset URLs",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            filenames,
            document_folder: documentFolder || "/",
          }),
        },
      );
      return response.assets || {};
    },
    [authFetch],
  );
};

// ============================================================================
// Document Publishing Hooks
// ============================================================================

/**
 * Response from access-check endpoint.
 */
export interface DocumentAccessInfo {
  id: string;
  public_doc_id: string;
  is_published: boolean;
  is_public: boolean;
  publish_date?: string;
  published_by?: string;
  can_publish: boolean;
  access_level: "private" | "published_org" | "published_public";
  /** True when the published document is protected by a recipient-facing
   *  password. Only meaningful when `is_published === true`. */
  password_protected: boolean;
  /** ISO 8601 timestamp; only present when password_protected is true. */
  password_set_at?: string;
  /** User id of the editor who set the current password. */
  password_set_by?: string;
}

/**
 * Hook to check document access/publish state.
 */
export const useGetDocumentAccess = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (workspaceId: string, docId: string): Promise<DocumentAccessInfo> => {
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/access-check?workspace_id=${workspaceId}`,
        "fetch document access state",
      );
    },
    [authFetch],
  );
};

/**
 * Hook to toggle document publish state.
 */
export const useUpdateDocumentPublish = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
      publish: boolean,
    ): Promise<DocumentAccessInfo> => {
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/publish?workspace_id=${workspaceId}`,
        "update document publish state",
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ publish }),
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to toggle document public state.
 */
export const useUpdateDocumentPublic = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
      is_public: boolean,
    ): Promise<DocumentAccessInfo> => {
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/public?workspace_id=${workspaceId}`,
        "update document public state",
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_public }),
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to set, change, or clear the document password (delivery gate).
 *
 * Pass `password=null` to clear protection; pass a non-null string
 * (>= 12 chars per NIST SP 800-63B) to set a new password. The BFF
 * rejects shorter passwords with HTTP 400. The plaintext is hashed
 * server-side via werkzeug.security scrypt; nothing crosses the wire
 * unencrypted other than the TLS-protected request body.
 */
export const useUpdateDocumentPassword = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      docId: string,
      password: string | null,
    ): Promise<DocumentAccessInfo> => {
      const encodedDocId = encodeURIComponent(docId);
      return await authFetch(
        `${API_BASE}/ida/documents/${encodedDocId}/password?workspace_id=${workspaceId}`,
        password === null
          ? "remove document password"
          : "set document password",
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        },
      );
    },
    [authFetch],
  );
};

/**
 * Hook to trigger a SharePoint sync for the workspace.
 * Called automatically on Documents page load when SharePoint is enabled for the workspace.
 * Returns a summary of what changed (new, updated, deleted, failed, unchanged counts).
 */
export const useSharePointSync = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
    ): Promise<{
      new: number;
      updated: number;
      deleted: number;
      failed: number;
      unchanged: number;
      errors: string[];
    }> => {
      return await authFetch(
        `${API_BASE}/ida/documents/sharepoint-sync?workspace_id=${workspaceId}`,
        "trigger SharePoint sync",
        { method: "POST" },
        60000, // Longer timeout — sync downloads files
      );
    },
    [authFetch],
  );
};

// Note: Chat functionality (streaming, conversations, messages) is now provided by
// @metalmind-ab/mmagent-chat-ui. The following hooks have been removed:
// - useStreamChat
// - useListConversations
// - useGetConversationMessages
// - useDeleteConversation
//
// Authentication headers are provided by src/modules/ida/hooks/useAuthHeaders.ts
